import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { portalApi, resolveFileUrl, isStoredFileUrl } from '../../../../lib/portalApi';
import KanbanBoard, { statusChip } from '../../shared/KanbanBoard';
import BatchTaskDetailPanel from '../../shared/BatchTaskDetailPanel';
import ConfirmDeleteModal from '../../shared/ConfirmDeleteModal';
import {
  FolderOpen,
  ExternalLink,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  Lock,
  Unlock,
  FileText,
  Calendar,
  Layers,
  ShieldCheck,
  UserPlus,
} from 'lucide-react';
import { SearchableDocumentsModal } from '../../shared/SearchableStartupDocuments';
import StartupAccessApprovalModal from '../components/StartupAccessApprovalModal';

const TABS = [
  { id: 'profile', label: 'Company profile' },
  { id: 'onboarding', label: 'Onboarding' },
  { id: 'dd', label: 'Due Diligence' },
  { id: 'active', label: 'Active' },
  { id: 'upcoming', label: 'Upcoming' },
];

const TASK_COLUMNS = [
  { id: 'todo', title: 'To do' },
  { id: 'in_progress', title: 'In progress' },
  { id: 'waiting', title: 'Waiting' },
  { id: 'review', title: 'Review' },
  { id: 'done', title: 'Done' },
];

function taskColumnId(status) {
  if (status === 'todo') return 'todo';
  if (status === 'in_progress') return 'in_progress';
  if (['clarification', 'blocked', 'on_hold'].includes(status)) return 'waiting';
  if (['ready_for_review', 'rework'].includes(status)) return 'review';
  if (['completed', 'cancelled'].includes(status)) return 'done';
  return 'todo';
}

async function transitionToward(task, toColumn) {
  const s = task.status;
  if (taskColumnId(s) === toColumn) return;

  const targetByColumn = {
    todo: 'todo',
    in_progress: 'in_progress',
    waiting: 'on_hold',
    review: 'ready_for_review',
    done: 'completed',
  };
  const next = targetByColumn[toColumn];
  if (!next || next === s) return;

  const reason =
    toColumn === 'waiting'
      ? 'Moved on board'
      : toColumn === 'todo' && ['completed', 'cancelled'].includes(s)
        ? 'Reopened from board'
        : undefined;

  await portalApi.transitionTask(task.id, next, reason);
}

function ActiveTasksPanel({ startupId, members, founderId, founderName, founderEmail }) {
  const todayKolkata = () =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());

  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState('');
  const [view, setView] = useState('kanban');
  const [kind, setKind] = useState('daily');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [taskDate, setTaskDate] = useState(todayKolkata());
  const [assigneeId, setAssigneeId] = useState(founderId || '');
  const [deadline, setDeadline] = useState('');
  const [meeting, setMeeting] = useState({
    mode: 'online',
    scheduledAt: '',
    meetingLink: '',
    location: '',
  });
  const [docFile, setDocFile] = useState(null);
  const [reqExtra, setReqExtra] = useState({ instructions: '', requiresSignature: false });
  const [createOpen, setCreateOpen] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const assigneeOptions = useMemo(() => {
    const opts = (members || []).filter((m) => m.userId);
    if (founderId && !opts.some((m) => String(m.userId) === String(founderId))) {
      opts.unshift({
        userId: founderId,
        name: founderName || 'Founder',
        email: founderEmail || '',
        memberRole: 'lead_founder',
      });
    }
    return opts;
  }, [members, founderId, founderName, founderEmail]);

  const nameFor = (uid) => {
    const m = assigneeOptions.find((x) => String(x.userId) === String(uid));
    return m?.name || m?.email || 'Unassigned';
  };

  const load = () =>
    portalApi.startupBatchTasks(startupId).then(setTasks).catch((e) => setError(e.message));

  useEffect(() => {
    load();
  }, [startupId]);

  useEffect(() => {
    if (founderId) setAssigneeId(founderId);
  }, [founderId]);

  const run = async (fn) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const create = () =>
    run(async () => {
      if (!title.trim()) throw new Error('Title required');
      if (kind === 'document_sent') {
        if (!docFile) throw new Error('File required');
        const fd = new FormData();
        fd.append('file', docFile);
        fd.append('title', title.trim());
        fd.append('description', description);
        if (assigneeId) fd.append('assigneeId', assigneeId);
        if (deadline) fd.append('deadline', new Date(deadline).toISOString());
        await portalApi.createDocumentSentTask(startupId, fd);
      } else {
        const body = {
          kind,
          title: title.trim(),
          description,
          assigneeId: assigneeId || undefined,
          deadline: deadline ? new Date(deadline).toISOString() : undefined,
          taskDate: kind === 'daily' ? (taskDate || todayKolkata()) : undefined,
          instructions: reqExtra.instructions,
          requiresSignature: reqExtra.requiresSignature,
        };
        if (kind === 'meeting') {
          Object.assign(body, {
            mode: meeting.mode,
            scheduledAt: meeting.scheduledAt
              ? new Date(meeting.scheduledAt).toISOString()
              : undefined,
            meetingLink: meeting.meetingLink,
            location: meeting.location,
          });
        }
        await portalApi.createBatchTask(startupId, body);
      }
      setTitle('');
      setDescription('');
      setDocFile(null);
      setDeadline('');
      setTaskDate(todayKolkata());
      setCreateOpen(false);
    });

  const deleteTask = (t) => {
    if (!window.confirm(`Delete task “${t.title}”? This cannot be undone.`)) return;
    run(async () => {
      await portalApi.deleteBatchTask(t.id);
      if (activeTaskId === t.id) setActiveTaskId(null);
    });
  };

  const kanbanItems = tasks.map((t) => ({
    ...t,
    columnId: taskColumnId(t.status),
  }));

  const renderTaskCard = (t) => {
    const isDaily = t.kind === 'daily' || t.isDaily;
    const assignedByText =
      t.assignedByName ||
      t.assignedBy?.name ||
      (t.assignedById ? 'Administrator' : 'Not recorded');

    return (
      <button
        type="button"
        className="space-y-1.5 w-full text-left"
        onClick={(e) => {
          e.stopPropagation();
          setActiveTaskId(t.id);
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="text-sm text-white font-medium leading-snug">
          {t.code ? <span className="text-white/35 mr-1">{t.code}</span> : null}
          {t.title}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {statusChip(t.status)}
          {isDaily ? (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#d42b92]/20 text-[#ff8ec5] border border-[#d42b92]/40">
              Daily
            </span>
          ) : (
            <span className="text-[10px] text-white/35 uppercase">
              {(t.kind || '').replace(/_/g, ' ')}
            </span>
          )}
        </div>
        {isDaily && (
          <div className="text-[11px] text-white/60 truncate">
            Assigned by: <span className="text-white/90 font-medium">{assignedByText}</span>
          </div>
        )}
        {t.taskDate && isDaily && (
          <div className="text-[10px] text-white/45">
            Date: {new Date(t.taskDate).toLocaleDateString()}
          </div>
        )}
        {(t.deadline || t.dueAt) && (
          <div className="text-[10px] text-[#ff8ec5]/80">
            Due {new Date(t.deadline || t.dueAt).toLocaleDateString()}
          </div>
        )}
        <select
          value={t.assigneeId || ''}
          onChange={(e) => {
            e.stopPropagation();
            if (busy) return;
            run(() => portalApi.patchBatchTask(t.id, { assigneeId: e.target.value || null }));
          }}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          disabled={busy}
          className="w-full mt-1 bg-black/50 border border-white/10 rounded-lg px-2 py-1 text-[11px] text-white/80 disabled:opacity-50"
        >
          <option value="">Unassigned</option>
          {assigneeOptions.map((m) => (
            <option key={m.userId} value={m.userId}>
              {m.name || m.email}
            </option>
          ))}
        </select>
      </button>
    );
  };

  const taskActions = (t) => (
    <div className="flex flex-wrap gap-2 pt-1">
      {t.documentRequest?.submittedUrl && (
        <a
          href={resolveFileUrl(t.documentRequest.submittedUrl)}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-[#ff8ec5]"
        >
          Open submission
        </a>
      )}
      {t.sentDocument?.url && (
        <a
          href={resolveFileUrl(t.sentDocument.url)}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-[#ff8ec5]"
        >
          Open sent file
        </a>
      )}
      {t.documentRequest?.status === 'submitted' && (
        <>
          <button
            type="button"
            disabled={busy}
            className="text-xs text-emerald-400 disabled:opacity-40"
            onClick={() => run(() => portalApi.reviewBatchDocRequest(t.id, 'approved'))}
          >
            Approve doc
          </button>
          <button
            type="button"
            disabled={busy}
            className="text-xs text-red-400 disabled:opacity-40"
            onClick={() =>
              run(() =>
                portalApi.reviewBatchDocRequest(
                  t.id,
                  'rejected',
                  window.prompt('Feedback?') || 'Please revise'
                )
              )
            }
          >
            Reject doc
          </button>
        </>
      )}
      {t.status === 'ready_for_review' && t.kind !== 'document_request' && (
        <>
          <button
            type="button"
            disabled={busy}
            className="text-xs text-emerald-400 disabled:opacity-40"
            onClick={() => run(() => portalApi.transitionTask(t.id, 'completed'))}
          >
            Complete
          </button>
          <button
            type="button"
            disabled={busy}
            className="text-xs text-amber-300 disabled:opacity-40"
            onClick={() =>
              run(() =>
                portalApi.transitionTask(
                  t.id,
                  'rework',
                  undefined,
                  window.prompt('Feedback?') || 'Needs rework'
                )
              )
            }
          >
            Rework
          </button>
        </>
      )}
      {!['completed', 'cancelled'].includes(t.status) && (
        <button
          type="button"
          disabled={busy}
          className="text-xs text-white/35 disabled:opacity-40"
          onClick={() =>
            run(() =>
              portalApi.transitionTask(t.id, 'cancelled', window.prompt('Reason?') || 'cancelled')
            )
          }
        >
          Cancel
        </button>
      )}
      <button
        type="button"
        disabled={busy}
        className="text-xs text-red-400/90 disabled:opacity-40"
        onClick={() => deleteTask(t)}
      >
        Delete
      </button>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-white font-medium">Active work</h3>
          <p className="text-xs text-white/40 mt-0.5">
            Drag cards to update status. Assign founders from each card.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setView('list')}
            className={`text-xs px-2.5 py-1 rounded-lg border ${
              view === 'list' ? 'border-[#d42b92] text-white' : 'border-white/15 text-white/50'
            }`}
          >
            List
          </button>
          <button
            type="button"
            onClick={() => setView('kanban')}
            className={`text-xs px-2.5 py-1 rounded-lg border ${
              view === 'kanban' ? 'border-[#d42b92] text-white' : 'border-white/15 text-white/50'
            }`}
          >
            Kanban
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen((v) => !v)}
            className="text-xs px-3 py-1.5 rounded-lg bg-[#d42b92] text-white"
          >
            {createOpen ? 'Close' : 'Create'}
          </button>
        </div>
      </div>

      {createOpen && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
          <div className="text-white font-medium text-sm">New deliverable</div>
          <div className="flex flex-wrap gap-2">
            {[
              ['daily', 'Daily task'],
              ['milestone', 'Milestone'],
              ['meeting', 'Meeting'],
              ['document_sent', 'Send doc'],
              ['document_request', 'Request doc'],
            ].map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                className={`px-3 py-1.5 rounded-lg text-xs border ${
                  kind === k
                    ? 'border-[#d42b92] text-white bg-[#d42b92]/20'
                    : 'border-white/15 text-white/70'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title"
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description / instructions"
            rows={2}
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
          />
          {kind === 'daily' ? (
            <div className="space-y-2">
              <div className="grid sm:grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] text-white/50 mb-1">Task date (defaults to today)</label>
                  <input
                    type="date"
                    value={taskDate}
                    onChange={(e) => setTaskDate(e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-white/50 mb-1">Optional due time / deadline</label>
                  <input
                    type="datetime-local"
                    value={deadline}
                    onChange={(e) => setDeadline(e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[11px] text-white/50 mb-1">Assignee</label>
                <select
                  value={assigneeId}
                  onChange={(e) => setAssigneeId(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                >
                  <option value="">Assignee…</option>
                  {assigneeOptions.map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.name || m.email} ({m.memberRole})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <select
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
              >
                <option value="">Assignee…</option>
                {assigneeOptions.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.name || m.email} ({m.memberRole})
                  </option>
                ))}
              </select>
              <input
                type="datetime-local"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
              />
            </div>
          )}
          {kind === 'meeting' && (
            <div className="grid md:grid-cols-2 gap-2">
              <select
                value={meeting.mode}
                onChange={(e) => setMeeting({ ...meeting, mode: e.target.value })}
                className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
              >
                <option value="online">Online</option>
                <option value="offline">Offline</option>
              </select>
              <input
                type="datetime-local"
                value={meeting.scheduledAt}
                onChange={(e) => setMeeting({ ...meeting, scheduledAt: e.target.value })}
                className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
              />
              {meeting.mode === 'online' ? (
                <input
                  placeholder="Meeting link"
                  value={meeting.meetingLink}
                  onChange={(e) => setMeeting({ ...meeting, meetingLink: e.target.value })}
                  className="md:col-span-2 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                />
              ) : (
                <input
                  placeholder="Location"
                  value={meeting.location}
                  onChange={(e) => setMeeting({ ...meeting, location: e.target.value })}
                  className="md:col-span-2 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                />
              )}
            </div>
          )}
          {kind === 'document_sent' && (
            <input
              type="file"
              onChange={(e) => setDocFile(e.target.files?.[0] || null)}
              className="text-sm text-white/60"
            />
          )}
          {kind === 'document_request' && (
            <div className="space-y-2">
              <textarea
                placeholder="Instructions"
                value={reqExtra.instructions}
                onChange={(e) => setReqExtra({ ...reqExtra, instructions: e.target.value })}
                rows={2}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
              />
              <label className="flex items-center gap-2 text-sm text-white/60">
                <input
                  type="checkbox"
                  checked={reqExtra.requiresSignature}
                  onChange={(e) =>
                    setReqExtra({ ...reqExtra, requiresSignature: e.target.checked })
                  }
                />
                Requires signature
              </label>
            </div>
          )}
          <button
            type="button"
            onClick={create}
            disabled={busy}
            className="px-3 py-2 rounded-xl bg-[#d42b92] text-white text-sm disabled:opacity-50 disabled:cursor-wait"
          >
            {busy ? 'Working…' : 'Create & assign'}
          </button>
        </div>
      )}

      {error && <p className="text-red-400 text-sm">{error}</p>}
      {busy && (
        <p className="text-xs text-[#ff8ec5]/80" aria-live="polite">
          Please wait, request in progress…
        </p>
      )}

      {view === 'kanban' ? (
        <KanbanBoard
          columns={TASK_COLUMNS}
          items={kanbanItems}
          disabled={busy}
          className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
          renderCard={renderTaskCard}
          onMove={(itemId, _from, to) =>
            run(async () => {
              const task = tasks.find((t) => t.id === itemId);
              if (!task) return;
              await transitionToward(task, to);
            })
          }
        />
      ) : (
        <div className="space-y-3">
          {tasks.length === 0 && <p className="text-white/40 text-sm">No active tasks yet.</p>}
          {tasks.map((t) => {
            const isDaily = t.kind === 'daily' || t.isDaily;
            const assignedByText =
              t.assignedByName ||
              t.assignedBy?.name ||
              (t.assignedById ? 'Administrator' : 'Not recorded');

            return (
              <div
                key={t.id}
                className="rounded-xl border border-white/10 px-4 py-3 space-y-2 cursor-pointer hover:border-[#d42b92]/40"
                onClick={() => setActiveTaskId(t.id)}
                onKeyDown={(e) => e.key === 'Enter' && setActiveTaskId(t.id)}
                role="button"
                tabIndex={0}
              >
                <div className="flex justify-between gap-2">
                  <div>
                    <div className="text-white font-medium flex items-center gap-2 flex-wrap">
                      {t.code ? <span className="text-white/40">{t.code} ·</span> : null}
                      <span>{t.title}</span>
                      {isDaily && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#d42b92]/20 text-[#ff8ec5] border border-[#d42b92]/40">
                          Daily
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-white/40 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                      <span>{isDaily ? 'Daily task' : t.kind} · {nameFor(t.assigneeId)}</span>
                      {isDaily && (
                        <span>
                          · Assigned by: <strong className="text-white/80 font-normal">{assignedByText}</strong>
                        </span>
                      )}
                      {t.taskDate && isDaily && (
                        <span>· Date: {new Date(t.taskDate).toLocaleDateString()}</span>
                      )}
                      {t.deadline || t.dueAt ? (
                        <span>· due {new Date(t.deadline || t.dueAt).toLocaleDateString()}</span>
                      ) : null}
                    </div>
                  </div>
                  {statusChip(t.status)}
                </div>
              <select
                value={t.assigneeId || ''}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) =>
                  run(() => portalApi.patchBatchTask(t.id, { assigneeId: e.target.value || null }))
                }
                disabled={busy}
                className="bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-xs text-white disabled:opacity-50"
              >
                <option value="">Unassigned</option>
                {assigneeOptions.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.name || m.email}
                  </option>
                ))}
              </select>
              <div onClick={(e) => e.stopPropagation()}>{taskActions(t)}</div>
            </div>
          );
        })}
        </div>
      )}

      {activeTaskId && (
        <BatchTaskDetailPanel
          taskId={activeTaskId}
          isAdmin
          assigneeOptions={assigneeOptions}
          onClose={() => setActiveTaskId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

export default function AdminStartupDossierPage() {
  const { batchId, startupId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'active';
  const [startup, setStartup] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [profile, setProfile] = useState(null);
  const [dd, setDd] = useState(null);
  const [error, setError] = useState('');
  const [momDrafts, setMomDrafts] = useState({});
  const [momBusy, setMomBusy] = useState('');

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [ddBusy, setDdBusy] = useState(false);
  const [ddStageFilter, setDdStageFilter] = useState('ALL');

  const reloadStartup = () => {
    portalApi.getBatchStartup(batchId, startupId).then(setStartup).catch(() => {});
  };

  const refreshDd = () => {
    return portalApi.getDdStartup(startupId).then(setDd).catch(() => setDd(null));
  };

  const handleOpenOrRefreshDd = async (reopen = false) => {
    setDdBusy(true);
    setError('');
    try {
      await portalApi.openDd(startupId, { reopen });
      await refreshDd();
    } catch (err) {
      setError(err.message || 'Failed to open/refresh Due Diligence');
    } finally {
      setDdBusy(false);
    }
  };

  const handleSetDdStatus = async (status) => {
    setDdBusy(true);
    setError('');
    try {
      await portalApi.setDdStatus(startupId, status);
      await refreshDd();
    } catch (err) {
      setError(err.message || 'Failed to update DD status');
    } finally {
      setDdBusy(false);
    }
  };

  const reloadMeetings = async () => {
    const [ob, ts] = await Promise.all([
      portalApi.onboardingBoard(startupId).catch(() => null),
      portalApi.startupBatchTasks(startupId).catch(() => []),
    ]);
    setOnboarding(ob);
    setTasks(ts || []);
  };

  useEffect(() => {
    portalApi
      .getBatchStartup(batchId, startupId)
      .then(setStartup)
      .catch((e) => setError(e.message));
    portalApi.onboardingBoard(startupId).then(setOnboarding).catch(() => setOnboarding(null));
    portalApi.startupBatchTasks(startupId).then(setTasks).catch(() => setTasks([]));
    portalApi.adminCompanyProfile(startupId).then(setProfile).catch(() => setProfile(null));
    portalApi.getDdStartup(startupId).then(setDd).catch(() => setDd(null));
  }, [batchId, startupId]);

  const confirmDeleteStartup = async () => {
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await portalApi.deleteStartup(startupId);
      setDeleteOpen(false);
      navigate(batchId ? `/admin/batches/${batchId}` : '/admin/startups');
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete startup');
    } finally {
      setDeleteBusy(false);
    }
  };

  const upcomingMeetings = useMemo(() => {
    const fromOnboarding = (onboarding?.steps || []).flatMap((s) =>
      (s.meetings || []).map((m) => ({
        ...m,
        source: 'onboarding',
        stepTitle: s.title,
      }))
    );
    const fromActive = tasks
      .filter((t) => t.kind === 'meeting' && t.meeting)
      .map((t) => ({
        ...t.meeting,
        source: 'active',
        taskId: t.id,
        status: t.status,
        title: t.meeting.title || t.title,
      }));
    return [...fromOnboarding, ...fromActive].sort((a, b) => {
      const da = a.scheduledAt ? new Date(a.scheduledAt).getTime() : 0;
      const db = b.scheduledAt ? new Date(b.scheduledAt).getTime() : 0;
      return da - db;
    });
  }, [onboarding, tasks]);

  const saveMom = async (m) => {
    const key = `${m.source}-${m.id}`;
    const mom = momDrafts[key] !== undefined ? momDrafts[key] : m.mom || '';
    setMomBusy(key);
    setError('');
    try {
      if (m.source === 'onboarding') {
        await portalApi.updateMeeting(m.id, { mom });
      } else {
        await portalApi.updateBatchMeeting(m.taskId, { mom });
      }
      setMomDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      await reloadMeetings();
    } catch (e) {
      setError(e.message);
    } finally {
      setMomBusy('');
    }
  };

  if (error && !startup) return <p className="text-red-400">{error}</p>;
  if (!startup) return <p className="text-white/40">Loading…</p>;

  return (
    <div className="space-y-6">
      <Link to={`/admin/batches/${batchId}`} className="text-sm text-white/50 hover:text-[#ff8ec5]">
        ← Batch
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-white">{startup.name}</h1>
          <p className="text-white/50 text-sm">
            {startup.founderName} · {startup.founderEmail} · {startup.stage}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowDocsModal(true)}
            className="px-3.5 py-2 rounded-xl border border-white/15 hover:border-[#d42b92]/50 hover:bg-[#d42b92]/10 text-white text-sm flex items-center gap-1.5 transition-colors"
          >
            <FolderOpen className="w-4 h-4 text-[#ff8ec5]" />
            All Documents
          </button>
          <button
            type="button"
            onClick={() => setShowAccessModal(true)}
            className="px-3.5 py-2 rounded-xl border border-[#d42b92]/40 bg-[#321129] text-white text-sm flex items-center gap-1.5 hover:bg-[#d42b92]/30 transition-colors"
          >
            <ShieldCheck className="w-4 h-4 text-[#ff8ec5]" />
            <span>Dashboard Access</span>
            {startup.dashboardAccess?.status === 'active' && (
              <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />
            )}
          </button>
          <Link
            to={`/admin/invites?role=founder&startupId=${startupId}&email=${encodeURIComponent(startup.founderEmail || '')}`}
            className="px-3.5 py-2 rounded-xl border border-[#d42b92]/60 bg-[#d42b92]/20 hover:bg-[#d42b92]/40 text-white text-sm flex items-center gap-1.5 transition-colors"
            title="Generate secure onboarding invite for startup founder"
          >
            <UserPlus className="w-4 h-4 text-[#ff8ec5]" />
            <span>Invite Founder</span>
          </Link>
          <Link
            to={`/admin/onboarding/${startupId}`}
            className="px-3 py-2 rounded-xl border border-white/15 text-white text-sm hover:border-white/30 transition-colors"
          >
            Onboarding workspace
          </Link>
          <button
            type="button"
            disabled={ddBusy}
            onClick={() => {
              setTab('dd');
              if (!dd || dd.status === 'closed') {
                handleOpenOrRefreshDd(false);
              }
            }}
            className="px-3 py-2 rounded-xl border border-white/15 text-white text-sm hover:border-[#d42b92]/50 transition-colors flex items-center gap-1.5"
          >
            <span>{dd && dd.status !== 'closed' ? 'DD Workspace' : 'Open DD'}</span>
            {dd?.status && dd.status !== 'closed' && (
              <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />
            )}
          </button>
          <button
            type="button"
            onClick={() => {
              setDeleteOpen(true);
              setDeleteError('');
            }}
            className="px-3 py-2 rounded-xl border border-red-500/30 text-red-400 text-sm hover:bg-red-500/10 transition-colors"
          >
            Delete Startup
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-white/10 pb-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setSearchParams({ tab: t.id })}
            className={`px-3 py-1.5 rounded-lg text-sm ${
              tab === t.id ? 'bg-[#d42b92]/25 text-white' : 'text-white/50 hover:text-white'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'profile' && (
        <div className="space-y-4">
          {startup.logo && (
            <img src={resolveFileUrl(startup.logo)} alt="" className="h-16 w-16 object-contain rounded-lg bg-white/5" />
          )}
          <div className="rounded-xl border border-white/10 p-4 space-y-3">
            <h3 className="text-white font-medium">Profile answers</h3>
            {Object.entries(profile?.startup?.profileAnswers || startup.profileAnswers || {})
              .filter(([k]) => !k.endsWith('__meta'))
              .map(([k, v]) => {
                const q = (profile?.form?.questions || []).find((x) => x.id === k);
                const label = q?.label || k;
                const display =
                  isStoredFileUrl(v) ? (
                    <a href={resolveFileUrl(v)} target="_blank" rel="noreferrer" className="text-[#ff8ec5]">
                      Open file
                    </a>
                  ) : (
                    String(Array.isArray(v) ? v.join(', ') : v)
                  );
                return (
                  <div key={k}>
                    <div className="text-xs text-white/40">{label}</div>
                    <div className="text-sm text-white/80 mt-0.5">{display}</div>
                  </div>
                );
              })}
            {Object.keys(profile?.startup?.profileAnswers || startup.profileAnswers || {}).length === 0 && (
              <p className="text-white/40 text-sm">No profile answers yet.</p>
            )}
          </div>
          {(profile?.startup?.profileDocuments || []).length > 0 && (
            <div className="rounded-xl border border-white/10 p-4 space-y-2">
              <h3 className="text-white font-medium">Profile documents</h3>
              {(profile.startup.profileDocuments || []).map((d, i) => (
                <a
                  key={i}
                  href={resolveFileUrl(d.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="block text-sm text-[#ff8ec5]"
                >
                  {d.originalName || d.requirementId}
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'onboarding' && (
        <div className="space-y-3">
          <p className="text-sm text-white/50">
            Progress {onboarding?.percent ?? 0}% · stage {startup.stage}
          </p>
          <Link to={`/admin/onboarding/${startupId}`} className="text-sm text-[#ff8ec5]">
            Open full workspace →
          </Link>
          <div className="space-y-2">
            {(onboarding?.steps || []).map((s) => (
              <div key={s.id} className="rounded-xl border border-white/10 px-3 py-2 flex justify-between">
                <span className="text-white text-sm">{s.title}</span>
                <span className="text-xs text-[#ff8ec5]">{s.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'dd' && (
        <div className="space-y-6">
          {!dd || dd.status === 'closed' ? (
            <div className="rounded-2xl border border-white/10 bg-white/[0.015] p-8 text-center space-y-4">
              <div className="w-12 h-12 rounded-2xl bg-[#d42b92]/10 border border-[#d42b92]/20 flex items-center justify-center mx-auto text-[#ff8ec5]">
                <FileText className="w-6 h-6" />
              </div>
              <div className="max-w-md mx-auto space-y-1">
                <h3 className="text-lg font-semibold text-white">Due Diligence Workspace</h3>
                <p className="text-white/45 text-sm">
                  {dd?.status === 'closed'
                    ? 'Due Diligence for this startup is currently closed. You can reopen it to request fresh updates from the founder.'
                    : 'Due Diligence has not been initialized yet for this startup. Open DD to assign questionnaire items and document requirements.'}
                </p>
              </div>
              <button
                type="button"
                disabled={ddBusy}
                onClick={() => handleOpenOrRefreshDd(true)}
                className="px-5 py-2.5 rounded-xl bg-[#d42b92] hover:bg-[#d42b92]/90 text-white text-sm font-medium transition-colors shadow-lg shadow-[#d42b92]/25 inline-flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${ddBusy ? 'animate-spin' : ''}`} />
                <span>
                  {dd?.status === 'closed' ? 'Reopen Due Diligence' : 'Initialize & Open Due Diligence'}
                </span>
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Status & Pack Header Card */}
              <div className="rounded-2xl border border-white/10 bg-white/[0.015] p-5 flex flex-wrap items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <span className="text-white font-medium text-base">
                      {dd.packName || 'Due Diligence Pack'}
                    </span>
                    {dd.status === 'submitted' ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-medium">
                        <CheckCircle className="w-3.5 h-3.5" />
                        Submitted · Ready for Review
                      </span>
                    ) : dd.status === 'under_review' ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-300 text-xs font-medium">
                        <Clock className="w-3.5 h-3.5" />
                        Under Review
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-medium">
                        <Clock className="w-3.5 h-3.5" />
                        Open (Awaiting Founder Submission)
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-white/40 flex items-center gap-3 flex-wrap">
                    {dd.openedAt && (
                      <span>Opened: {new Date(dd.openedAt).toLocaleDateString()}</span>
                    )}
                    {dd.submittedAt && (
                      <span>
                        Submitted: {new Date(dd.submittedAt).toLocaleDateString()} at{' '}
                        {new Date(dd.submittedAt).toLocaleTimeString()}
                      </span>
                    )}
                    <span>{(dd.questions || []).length} questions</span>
                    <span>{(dd.documentRequirements || []).length} document requirements</span>
                  </div>
                </div>

                {/* Status Action Controls */}
                <div className="flex items-center gap-2 flex-wrap">
                  {dd.status === 'submitted' && (
                    <button
                      type="button"
                      disabled={ddBusy}
                      onClick={() => handleSetDdStatus('under_review')}
                      className="px-3 py-1.5 rounded-xl border border-purple-500/30 bg-purple-500/10 text-purple-300 hover:bg-purple-500/20 text-xs font-medium transition-colors"
                    >
                      Mark Under Review
                    </button>
                  )}
                  {['submitted', 'under_review'].includes(dd.status) && (
                    <button
                      type="button"
                      disabled={ddBusy}
                      onClick={() => handleOpenOrRefreshDd(true)}
                      className="px-3 py-1.5 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 text-xs font-medium transition-colors"
                    >
                      Reopen for Edits
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={ddBusy}
                    onClick={() => handleSetDdStatus('closed')}
                    className="px-3 py-1.5 rounded-xl border border-white/15 hover:border-white/30 text-white/80 hover:text-white text-xs font-medium transition-colors"
                  >
                    Close / Complete DD
                  </button>
                  <button
                    type="button"
                    disabled={ddBusy}
                    onClick={() => handleOpenOrRefreshDd(false)}
                    className="p-1.5 rounded-xl border border-white/10 hover:border-white/20 text-white/50 hover:text-white transition-colors"
                    title="Refresh questions & requirements from active pack"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${ddBusy ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>

              {/* Stage Filter Tabs */}
              {(dd.stages || []).length > 0 && (
                <div className="flex flex-wrap items-center gap-2 border-b border-white/10 pb-3">
                  <button
                    type="button"
                    onClick={() => setDdStageFilter('ALL')}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      ddStageFilter === 'ALL'
                        ? 'border-[#d42b92] bg-[#d42b92]/20 text-white font-medium shadow-sm shadow-[#d42b92]/20'
                        : 'border-white/10 text-white/50 hover:text-white/80'
                    }`}
                  >
                    All Sections ({(dd.questions || []).length} Qs)
                  </button>
                  {dd.stages.map((s, idx) => {
                    const qCount = (dd.questions || []).filter((q) => q.stageId === s.id).length;
                    const dCount = (dd.documentRequirements || []).filter((d) => d.stageId === s.id).length;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setDdStageFilter(s.id)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                          ddStageFilter === s.id
                            ? 'border-[#d42b92] bg-[#d42b92]/20 text-white font-medium shadow-sm shadow-[#d42b92]/20'
                            : 'border-white/10 text-white/50 hover:text-white/80'
                        }`}
                      >
                        Stage {idx + 1}: {s.title} ({qCount} Qs{dCount ? ` · ${dCount} docs` : ''})
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Questions & Answers */}
              <div className="rounded-2xl border border-white/10 bg-white/[0.015] p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-medium text-base">Questions & Responses</h3>
                  <span className="text-xs text-white/40">
                    {Object.keys(dd.answers || {}).length} answered
                  </span>
                </div>

                {(() => {
                  const qList = (dd.questions || []).filter((q) => {
                    if (ddStageFilter === 'ALL') return true;
                    return String(q.stageId) === String(ddStageFilter);
                  });

                  if (qList.length === 0) {
                    return (
                      <p className="text-white/40 text-sm italic">
                        No questions in this section.
                      </p>
                    );
                  }

                  return (
                    <div className="space-y-3.5">
                      {qList.map((q) => {
                        const rawAnswer = dd.answers?.[q.id];
                        const answer =
                          rawAnswer !== undefined && rawAnswer !== null
                            ? rawAnswer
                            : dd.labeledAnswers?.[q.label];
                        const hasAnswer = answer !== undefined && answer !== null && answer !== '';

                        return (
                          <div
                            key={q.id}
                            className="rounded-xl border border-white/5 bg-black/25 p-3.5 space-y-1.5"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-xs text-white/50 font-medium">
                                {q.label} {q.required && <span className="text-[#ff8ec5]">*</span>}
                              </div>
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 font-mono text-white/40">
                                {q.type}
                              </span>
                            </div>
                            {q.helpText && (
                              <div className="text-xs text-white/35">{q.helpText}</div>
                            )}
                            <div className="pt-1">
                              {hasAnswer ? (
                                isStoredFileUrl(answer) ? (
                                  <a
                                    href={resolveFileUrl(answer)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1.5 text-sm text-[#ff8ec5] hover:underline"
                                  >
                                    <span>View uploaded file</span>
                                    <ExternalLink className="w-3.5 h-3.5" />
                                  </a>
                                ) : (
                                  <div className="text-sm text-white/90 whitespace-pre-wrap">
                                    {Array.isArray(answer) ? answer.join(', ') : String(answer)}
                                  </div>
                                )
                              ) : (
                                <span className="text-xs text-white/30 italic">Not answered yet</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Document Requirements & Uploads */}
              <div className="rounded-2xl border border-white/10 bg-white/[0.015] p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-medium text-base">Required Documents</h3>
                  <span className="text-xs text-white/40">
                    {(dd.documents || []).length} uploaded
                  </span>
                </div>

                {(() => {
                  const dList = (dd.documentRequirements || []).filter((d) => {
                    if (ddStageFilter === 'ALL') return true;
                    return String(d.stageId) === String(ddStageFilter);
                  });

                  if (dList.length === 0 && (dd.documents || []).length === 0) {
                    return (
                      <p className="text-white/40 text-sm italic">
                        No document requirements in this section.
                      </p>
                    );
                  }

                  return (
                    <div className="space-y-3">
                      {dList.map((d) => {
                        const uploaded = (dd.documents || []).find(
                          (x) => String(x.requirementId) === String(d.id) || x.label === d.label
                        );

                        return (
                          <div
                            key={d.id}
                            className="rounded-xl border border-white/5 bg-black/25 p-3.5 flex flex-wrap items-center justify-between gap-3"
                          >
                            <div className="min-w-0 space-y-0.5">
                              <div className="text-sm font-medium text-white flex items-center gap-2">
                                <span>{d.label}</span>
                                {d.required && <span className="text-[#ff8ec5] text-xs">*</span>}
                              </div>
                              {d.description && (
                                <div className="text-xs text-white/40">{d.description}</div>
                              )}
                              <div className="text-[11px] text-white/35">
                                Accepts: {d.accept || 'any files'}
                              </div>
                            </div>

                            <div>
                              {uploaded ? (
                                <a
                                  href={resolveFileUrl(uploaded.url)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 text-xs font-medium transition-colors"
                                >
                                  <FileText className="w-3.5 h-3.5" />
                                  <span>{uploaded.originalName || 'Download Uploaded File'}</span>
                                  <ExternalLink className="w-3 h-3 text-emerald-400" />
                                </a>
                              ) : (
                                <span className="px-2.5 py-1 rounded-full bg-white/5 text-white/40 text-xs border border-white/5">
                                  Pending upload
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {/* Any extra documents uploaded without specific requirement */}
                      {(dd.documents || []).filter(
                        (uploaded) =>
                          !dList.some(
                            (d) =>
                              String(d.id) === String(uploaded.requirementId) ||
                              d.label === uploaded.label
                          )
                      ).map((extra, idx) => (
                        <div
                          key={idx}
                          className="rounded-xl border border-white/5 bg-black/25 p-3.5 flex items-center justify-between gap-3"
                        >
                          <div>
                            <div className="text-sm font-medium text-white">
                              {extra.label || extra.originalName || 'Supplemental File'}
                            </div>
                            <div className="text-xs text-white/40">Supplemental upload</div>
                          </div>
                          <a
                            href={resolveFileUrl(extra.url)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/15 hover:border-[#d42b92]/50 text-white text-xs"
                          >
                            <span>Open file</span>
                            <ExternalLink className="w-3 h-3 text-[#ff8ec5]" />
                          </a>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'active' && (
        <ActiveTasksPanel
          startupId={startupId}
          members={startup.members || []}
          founderId={startup.founderId}
          founderName={startup.founderName}
          founderEmail={startup.founderEmail}
        />
      )}

      {tab === 'upcoming' && (
        <div className="space-y-3">
          {upcomingMeetings.length === 0 && (
            <p className="text-white/40 text-sm">No upcoming meetings.</p>
          )}
          {upcomingMeetings.map((m) => {
            const key = `${m.source}-${m.id}`;
            const momValue = momDrafts[key] !== undefined ? momDrafts[key] : m.mom || '';
            return (
              <div key={key} className="rounded-xl border border-white/10 px-4 py-3 space-y-2">
                <div className="text-white">{m.title}</div>
                <div className="text-xs text-white/40">
                  {m.source}
                  {m.scheduledAt ? ` · ${new Date(m.scheduledAt).toLocaleString()}` : ''}
                  {m.stepTitle ? ` · ${m.stepTitle}` : ''}
                  {m.meetingLink ? (
                    <>
                      {' '}
                      ·{' '}
                      <a
                        href={m.meetingLink}
                        className="text-[#ff8ec5]"
                        target="_blank"
                        rel="noreferrer"
                      >
                        link
                      </a>
                    </>
                  ) : null}
                </div>
                <div className="text-xs text-white/50 uppercase tracking-wide pt-1">
                  Minutes of Meeting
                </div>
                <textarea
                  rows={3}
                  placeholder="Add MOM after the meeting…"
                  value={momValue}
                  disabled={momBusy === key}
                  onChange={(e) =>
                    setMomDrafts((prev) => ({ ...prev, [key]: e.target.value }))
                  }
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                />
                {momValue !== (m.mom || '') && (
                  <button
                    type="button"
                    disabled={momBusy === key}
                    onClick={() => saveMom(m)}
                    className="px-3 py-1.5 rounded-lg bg-[#d42b92] text-white text-xs disabled:opacity-40"
                  >
                    {momBusy === key ? 'Saving…' : 'Save MOM'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDeleteModal
        open={deleteOpen}
        onClose={() => {
          if (!deleteBusy) {
            setDeleteOpen(false);
            setDeleteError('');
          }
        }}
        onConfirm={confirmDeleteStartup}
        title="Delete Startup"
        entityName={startup?.name}
        entityDetail={`${startup?.founderName ? startup.founderName + ' · ' : ''}${startup?.founderEmail || ''} · Stage: ${startup?.stage || ''}`}
        description="Are you sure you want to delete this startup? This will permanently remove its profile, onboarding records, tasks, meetings, and documents."
        warning="Founder user accounts will NOT be deleted. Only startup program records are removed."
        busy={deleteBusy}
        error={deleteError}
        confirmLabel="Delete Startup"
      />

      <SearchableDocumentsModal
        isOpen={showDocsModal}
        onClose={() => setShowDocsModal(false)}
        startupId={startupId}
        title={`${startup?.name || 'Startup'} · Documents Repository`}
      />

      {showAccessModal && (
        <StartupAccessApprovalModal
          startupId={startupId}
          onClose={() => setShowAccessModal(false)}
          onUpdated={reloadStartup}
        />
      )}
    </div>
  );
}
